import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix for default marker icon
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

interface LocationPickerProps {
    onLocationSelect: (lat: number, lng: number) => void;
    initialLat?: number;
    initialLng?: number;
}

// Continental/Regional default locations
const getDefaultLocations = (t: (key: string) => string) => ({
    // North America
    northAmerica: {
        east: { lat: 40.7128, lng: -74.0060, name: t('locations.newYork') },
        central: { lat: 41.8781, lng: -87.6298, name: t('locations.chicago') },
        west: { lat: 34.0522, lng: -118.2437, name: t('locations.losAngeles') },
    },
    // Europe
    europe: {
        west: { lat: 48.8566, lng: 2.3522, name: t('locations.paris') },
        central: { lat: 52.5200, lng: 13.4050, name: t('locations.berlin') },
        east: { lat: 55.7558, lng: 37.6173, name: t('locations.moscow') },
    },
    // Asia
    asia: {
        east: { lat: 35.6762, lng: 139.6503, name: t('locations.tokyo') },
        southeast: { lat: 1.3521, lng: 103.8198, name: t('locations.singapore') },
        south: { lat: 28.6139, lng: 77.2090, name: t('locations.newDelhi') },
    },
    // Oceania
    oceania: {
        main: { lat: -33.8688, lng: 151.2093, name: t('locations.sydney') },
    },
    // South America
    southAmerica: {
        main: { lat: -23.5505, lng: -46.6333, name: t('locations.saoPaulo') },
    },
    // Africa
    africa: {
        north: { lat: 30.0444, lng: 31.2357, name: t('locations.cairo') },
        south: { lat: -33.9249, lng: 18.4241, name: t('locations.capeTown') },
    }
});

const getDefaultLocationByTimezone = (t: (key: string) => string): [number, number] => {
    const hour = new Date().getHours();
    const offset = new Date().getTimezoneOffset() / 60;
    const defaultLocations = getDefaultLocations(t);

    // North America (UTC-4 to UTC-8)
    if (offset >= 4 && offset <= 8) {
        if (offset >= 7) return [defaultLocations.northAmerica.west.lat, defaultLocations.northAmerica.west.lng];
        if (offset >= 5) return [defaultLocations.northAmerica.central.lat, defaultLocations.northAmerica.central.lng];
        return [defaultLocations.northAmerica.east.lat, defaultLocations.northAmerica.east.lng];
    }
    
    // Europe (UTC+0 to UTC+3)
    if (offset >= -3 && offset <= 0) {
        if (offset <= -2) return [defaultLocations.europe.east.lat, defaultLocations.europe.east.lng];
        if (offset <= -1) return [defaultLocations.europe.central.lat, defaultLocations.europe.central.lng];
        return [defaultLocations.europe.west.lat, defaultLocations.europe.west.lng];
    }
    
    // Asia (UTC+5 to UTC+9)
    if (offset >= -9 && offset <= -5) {
        if (offset <= -8) return [defaultLocations.asia.east.lat, defaultLocations.asia.east.lng];
        if (offset <= -7) return [defaultLocations.asia.southeast.lat, defaultLocations.asia.southeast.lng];
        return [defaultLocations.asia.south.lat, defaultLocations.asia.south.lng];
    }
    
    // Oceania (UTC+8 to UTC+12)
    if (offset >= -12 && offset <= -8) {
        return [defaultLocations.oceania.main.lat, defaultLocations.oceania.main.lng];
    }
    
    // South America (UTC-3 to UTC-5)
    if (offset >= 3 && offset <= 5) {
        return [defaultLocations.southAmerica.main.lat, defaultLocations.southAmerica.main.lng];
    }
    
    // Africa (UTC+0 to UTC+4)
    if (offset >= -4 && offset <= 0) {
        if (offset <= -2) return [defaultLocations.africa.south.lat, defaultLocations.africa.south.lng];
        return [defaultLocations.africa.north.lat, defaultLocations.africa.north.lng];
    }
    
    // Default to a central location if timezone doesn't match any region
    return [defaultLocations.europe.central.lat, defaultLocations.europe.central.lng];
};

// Enhanced function to get user's location
const getUserLocation = (): Promise<[number, number]> => {
    return new Promise((resolve, reject) => {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve([position.coords.latitude, position.coords.longitude]);
                },
                (error) => {
                    reject(error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0
                }
            );
        } else {
            reject(new Error("Geolocation not available"));
        }
    });
};

const MapCenterUpdater: React.FC<{ center: [number, number] }> = ({ center }) => {
    const map = useMap();
    useEffect(() => {
        map.setView(center, map.getZoom());
    }, [center, map]);
    return null;
};

const MapClickHandler: React.FC<{onLocationSelect: (lat: number, lng: number) => void}> = ({ onLocationSelect }) => {
    useMapEvents({
        click(e) {
            onLocationSelect(e.latlng.lat, e.latlng.lng);
        },
    });
    return null;
};

export const LocationPicker: React.FC<LocationPickerProps> = ({ 
    onLocationSelect,
    initialLat,
    initialLng
}) => {
    const { t } = useTranslation();
    const [position, setPosition] = useState<[number, number] | null>(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (position?.[0] === 0 && position?.[1] === 0) {
            // Attempt to get the user's location
            if ("geolocation" in navigator) {
                navigator.geolocation.getCurrentPosition(
                    (userPosition) => {
                        const { latitude, longitude } = userPosition.coords;
                        setPosition([latitude, longitude]);
                        onLocationSelect(latitude, longitude);
                    },
                    (error) => {
                        console.error(t('locationPicker.geolocationError'), error);
                        // Fall back to a default location
                        const [defaultLat, defaultLng] = getDefaultLocationByTimezone(t);
                        setPosition([defaultLat, defaultLng]);
                        onLocationSelect(defaultLat, defaultLng);
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 5000,
                        maximumAge: 0,
                    }
                );
            } else {
                console.warn(t('locationPicker.geolocationNotAvailable'));
                const [defaultLat, defaultLng] = getDefaultLocationByTimezone(t);
                setPosition([defaultLat, defaultLng]);
                onLocationSelect(defaultLat, defaultLng);
            }
        }
    
        if (position === null) {
            if (initialLat !== undefined && initialLng !== undefined && 
                initialLat !== 0 && initialLng !== 0) {
                setPosition([initialLat, initialLng]);
            } else {
                const [defaultLat, defaultLng] = getDefaultLocationByTimezone(t);
                setPosition([defaultLat, defaultLng]);
                onLocationSelect(defaultLat, defaultLng);
            }
            setLoading(false);
        }
    }, [initialLat, initialLng, onLocationSelect, position, t]);

    const handleLocationSelect = (lat: number, lng: number) => {
        setPosition([lat, lng]);
        onLocationSelect(lat, lng);
    };

    const handleSearch = async (e: React.FormEvent<HTMLButtonElement>) => {
        e.preventDefault(); // Prevent form submission
    
        if (!searchTerm.trim()) return;
    
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchTerm)}&format=json`
            );
            const results = await response.json();
            if (results.length > 0) {
                const { lat, lon } = results[0];
                handleLocationSelect(parseFloat(lat), parseFloat(lon));
            } else {
                alert(t('locationPicker.locationNotFound'));
            }
        } catch (error) {
            console.error('Error fetching location data:', error);
            alert(t('locationPicker.searchError'));
        }
    };

    if (loading || !position) {
        return <div>{t('locationPicker.loading')}</div>;
    }

    return (
        <div className="map-container">
            <div style={{ marginBottom: '10px' }}>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('locationPicker.searchPlaceholder')}
                    style={{ padding: '8px', width: 'calc(100% - 100px)', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <button
                    onClick={(e) => handleSearch(e)}
                    style={{
                        padding: '8px 16px',
                        marginLeft: '10px',
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: '#007bff',
                        color: 'white',
                        cursor: 'pointer',
                    }}
                >
                    {t('locationPicker.searchButton')}
                </button>
            </div>
            <MapContainer 
                center={position} 
                zoom={13} 
                style={{ height: '400px', width: '100%', borderRadius: '8px' }}
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <Marker position={position} />
                <MapClickHandler onLocationSelect={handleLocationSelect} />
                <MapCenterUpdater center={position} />
            </MapContainer>
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                {t('locationPicker.clickToSelectCompany')}
            </div>
        </div>
    );
};